/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import Box from '@mui/material/Box';
import { WalletDetails, currency_symbols } from '../../../typings/wallet';
import useQuery from '../../../hooks/useQuery';
import { walletDetailsAtom, walletModalOpenAtom } from '../../../states/wallet';
import { getWalletDetails } from '../../../requests/wallet-balance';
import Loader from '../loader';
import { QSButton, QSTypography } from '../../atoms';
import { openPopup } from '../../../utils/popup';
import requestConfig from '../../../config/request';
import { getFirebaseToken } from '../../../../infra/auth/firebase-auth';
import { shouldHideDTDetails } from '../../../../modules/login/v1/utility';
import WalletDetailsModal from './wallet-details-modal';
import { USER_STATUS_STORAGE_KEYS } from '../../../constants/storage';

const key = USER_STATUS_STORAGE_KEYS.LAST_REFRESHED_TIME_WALLET;
const refreshInterval = 5 * 60 * 1000;

const Wallet: FC<{ showExpandedView: boolean }> = ({ showExpandedView }) => {
  const {
    requestData: { data: walletData, loading },
    refreshData,
  } = useQuery<WalletDetails | undefined>({
    requestAtom: walletDetailsAtom,
    queryFunction: getWalletDetails,
  });

  const lastSuccessfulWalletDataRef = useRef<WalletDetails | undefined>(walletData);

  useEffect(() => {
    if (walletData) {
      lastSuccessfulWalletDataRef.current = walletData;
      localStorage.setItem(key, new Date().toISOString());
    }
  }, [walletData]);

  useEffect(() => {
    if (showExpandedView) {
      const last = localStorage.getItem(key);
      if (!last || Date.now() - Date.parse(last) > refreshInterval) {
        refreshData();
        localStorage.setItem(key, new Date().toISOString());
      }
    }
  }, [showExpandedView, refreshData]);

  const displayWalletData = walletData ?? lastSuccessfulWalletDataRef.current;

  const hideDTDetails = shouldHideDTDetails();
  const [isWalletModalOpen, setIsWalletModalOpen] = useAtom(walletModalOpenAtom);

  const handleOnClickRecharge = async () => {
    if (!displayWalletData || loading) return;
    if (displayWalletData.generatePaymentLink) {
      const token = await getFirebaseToken();
      openPopup(`${requestConfig.dtCallbackUrl}/wallet?token=${token}`, '_self');
    } else window.open(displayWalletData.rechargeLink, '_blank');
  };

  if (!displayWalletData) {
    return null;
  }

  return (
    <Box
      css={css`
        margin: 0 0 0 10px;
        display: flex;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        flex-wrap: nowrap;
        align-items: center;
        align-self: stretch;
        overflow: hidden;
        white-space: nowrap;
      `}
    >
      {loading ? (
        <Loader
          size={18}
          containerProps={{
            sx: {
              marginLeft: 'auto',
            },
          }}
        />
      ) : (
        <Box
          css={css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            column-gap: 16px;
          `}
        >
          <QSTypography
            css={css`
              font-size: 13px;
              font-weight: 500;
              cursor: pointer;
              &:hover {
                color: #3f906d;
                text-decoration: underline;
              }
            `}
            onClick={() => setIsWalletModalOpen(true)}
          >
            {currency_symbols[displayWalletData?.currencyCode] || displayWalletData?.currencyCode}
            {displayWalletData?.balance?.toFixed(2)}
          </QSTypography>
          {!hideDTDetails ? (
            <QSButton
              variant="contained"
              css={css`
                color: #3d906d;
                background-color: transparent;
                border: 1px solid #3d906d;
                border-radius: 4px;
                box-shadow: none;
                outline: none;
                cursor: pointer;
                height: 28px;
                padding: 6px 12px;
                display: flex;
                justify-content: center;
                align-items: center;
                column-gap: 12px;

                &:hover,
                &:focus,
                &:active {
                  color: #ffffff;
                  background-color: #3d906d;
                  box-shadow: none;
                  outline: none;
                }
              `}
              disabled={loading || !showExpandedView}
              onMouseDown={(event) => {
                // Note: This is added to prevent nested button ripple effect
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={handleOnClickRecharge}
              TouchRippleProps={{
                style: {
                  color: '#25254984',
                },
              }}
            >
              <QSTypography
                css={css`
                  font-size: 13px;
                  font-weight: 600;
                `}
              >
                Recharge
              </QSTypography>
            </QSButton>
          ) : null}
        </Box>
      )}
      {walletData && (
        <WalletDetailsModal
          open={isWalletModalOpen}
          onClose={() => setIsWalletModalOpen(false)}
          walletData={displayWalletData}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default Wallet;
